import { HealthSourceNote } from "@/components/health/HealthSourceInfo";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/lib/AuthContext";
import { normaliseTier } from "@/lib/membershipUtils";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Sparkles,
  SendHorizontal,
  Lock,
  RotateCcw,
  Bot,
  User as UserIcon,
  Loader2,
  Zap,
  Crown,
  Dumbbell,
  Save,
  Wand2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const API_BASE =
  process.env.REACT_APP_BACKEND_URL ||
  (typeof window !== "undefined" ? window.location.origin : "");

/** Tiers + roles allowed to OPEN the AI coach page. Momentum users are
 *  allowed in with a daily quota; the server enforces the actual ceiling
 *  and the page surfaces the remaining count. Foundation (legacy) stays
 *  locked. Trial users on Strength plan get Strength-equivalent access. */
const COACH_ACCESS = new Set(["momentum", "strength", "elite_coaching"]);

const SUGGESTED_PROMPTS = [
  "Design me a 3-day full-body workout for fat loss",
  "What's a good high-protein breakfast under 500 calories?",
  "How do I fix knee caving on squats?",
  "Plan a deload week after 6 weeks of pushing PRs",
];

const SYSTEM_PROMPT_OVERRIDE =
  "You are the TA Fitness AI Coach. You give specific, evidence-informed " +
  "advice on workouts, exercise form, programming, nutrition, recovery, and " +
  "motivation. Be concise (under 200 words). Use bullet points for lists. " +
  "When the user asks for a workout or meal plan, structure your answer " +
  "day-by-day. Always end with a single suggested follow-up question the " +
  "user could ask next, prefixed with 'Try asking: '.";

function suggestionsTestid(idx) {
  return `coach-suggestion-${idx}`;
}

export default function AICoach() {
  const { user } = useAuth();
  const tier = normaliseTier(user?.membership_type);
  // Trial users on Strength plan also pass. Sibling sends
  // membership_plan === "Strength" during trial, so check that path too.
  const onTrialStrength =
    user?.membership_status === "trial" && user?.membership_plan === "Strength";
  const hasAccess =
    COACH_ACCESS.has(tier) ||
    onTrialStrength ||
    user?.role === "admin" ||
    user?.role === "trainer";

  // Per-user persistent session id. Same id sent on every prompt keeps
  // the Anthropic conversation context server-side across page reloads.
  const sessionKey = useMemo(
    () => (user ? `ta_coach_session_${user.id || user._id}` : null),
    [user]
  );
  const messagesKey = useMemo(
    () => (user ? `ta_coach_msgs_${user.id || user._id}` : null),
    [user]
  );

  const [sessionId] = useState(() => {
    if (typeof window === "undefined" || !sessionKey) return null;
    const existing = window.localStorage.getItem(sessionKey);
    if (existing) return existing;
    const fresh = `coach_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    window.localStorage.setItem(sessionKey, fresh);
    return fresh;
  });

  // Messages persisted to localStorage so reloads don't wipe history.
  const [messages, setMessages] = useState(() => {
    if (typeof window === "undefined" || !messagesKey) return [];
    try {
      const raw = window.localStorage.getItem(messagesKey);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Daily quota state — populated on mount via /api/ai/coach/usage and
  // refreshed after every successful send. Null means we haven't fetched
  // yet (don't render the badge); when `limit` is null after fetch the
  // user is unlimited (Strength / Elite / Admin / Trial-Strength).
  const [usage, setUsage] = useState(null);
  // When the server returns 429 we flip this to true so the UI shows a
  // blocking upgrade dialog instead of an inline error. The dialog
  // links straight to the pricing page in a new tab.
  const [limitDialogOpen, setLimitDialogOpen] = useState(false);
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  // ---------------- Workout Plan Builder state ----------------------- //
  // Open the builder dialog. We start at step="brief" (user describes
  // their goal), generate -> step="preview" (show the AI's plan), and
  // save -> close + toast. Resets between opens.
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderStep, setBuilderStep] = useState("brief"); // "brief" | "preview"
  const [builderPrompt, setBuilderPrompt] = useState("");
  const [builderGenerating, setBuilderGenerating] = useState(false);
  const [builderSaving, setBuilderSaving] = useState(false);
  const [builderPlan, setBuilderPlan] = useState(null);
  const [builderError, setBuilderError] = useState("");

  // Builder is gated to unlimited tiers (Strength / Elite / admin /
  // trainer / trial-Strength). For Momentum users the button is hidden
  // — the backend would 403 anyway, but hiding the entry point is the
  // cleaner UX.
  const canBuildPlans =
    user?.role === "admin" ||
    user?.role === "trainer" ||
    tier === "strength" ||
    tier === "elite_coaching" ||
    onTrialStrength;

  // Best-effort browser timezone (e.g. "America/Los_Angeles"). Sent on
  // every AI Coach call so the daily quota rolls over on the user's
  // local midnight, not UTC. Memoised — `Intl.DateTimeFormat()` is
  // pricier than a string compare.
  const userTz = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
      return "UTC";
    }
  }, []);

  // Human-friendly label for the next quota rollover. Quotas reset at
  // the user's LOCAL midnight (server keys on `X-User-Timezone`), so
  // this is just "tomorrow 00:00 in the browser's clock", formatted
  // for the active locale (12h in en-US, 24h in en-GB / de-DE, etc.).
  // Refreshed every 60s so power users who keep the tab open across
  // midnight see the label flip without a page reload.
  const computeResetLabel = () => {
    try {
      const next = new Date();
      next.setHours(24, 0, 0, 0); // next local midnight
      return next.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    } catch {
      return "midnight";
    }
  };
  const [nextResetLabel, setNextResetLabel] = useState(computeResetLabel);
  useEffect(() => {
    const id = setInterval(() => setNextResetLabel(computeResetLabel()), 60_000);
    return () => clearInterval(id);
  }, []);

  // Fetch quota on mount and after each successful send.
  const fetchUsage = async () => {
    if (!hasAccess) return;
    try {
      const token = localStorage.getItem("ta_access_token");
      const r = await fetch(`${API_BASE}/api/ai/coach/usage`, {
        headers: {
          "X-User-Timezone": userTz,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (r.ok) setUsage(await r.json());
    } catch {
      /* non-fatal — badge just won't render */
    }
  };
  useEffect(() => {
    fetchUsage();
  // fetchUsage is stable across renders by design (we want it identity-
  // bound to user.id only); React's hook lint can't infer that.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Persist on every change so a page-close mid-conversation doesn't
  // drop the last message.
  useEffect(() => {
    if (typeof window === "undefined" || !messagesKey) return;
    window.localStorage.setItem(messagesKey, JSON.stringify(messages));
  }, [messages, messagesKey]);

  // Auto-scroll to the latest message whenever the thread grows.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, loading]);

  const sendPrompt = async (text) => {
    const body = (text ?? prompt).trim();
    if (!body || loading) return;
    setError("");
    setLoading(true);
    const nextMessages = [...messages, { role: "user", content: body, ts: Date.now() }];
    setMessages(nextMessages);
    setPrompt("");
    try {
      const token = localStorage.getItem("ta_access_token");
      const res = await fetch(`${API_BASE}/api/ai/invoke`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-User-Timezone": userTz,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          prompt: body,
          session_id: sessionId,
          system: SYSTEM_PROMPT_OVERRIDE,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Structured detail from the backend — surface intent (locked
        // vs limit-hit) so we can show the right CTA.
        const detail = data?.detail;
        const reason = (detail && typeof detail === "object" && detail.reason) || null;
        if (res.status === 429 && reason === "daily_limit_reached") {
          // Drop the user message so retry tomorrow doesn't double-post.
          setMessages((m) => m.slice(0, -1));
          setLimitDialogOpen(true);
          fetchUsage();
          return;
        }
        const message =
          (detail && typeof detail === "object" && detail.message) ||
          (typeof detail === "string" && detail) ||
          `Request failed (HTTP ${res.status})`;
        throw new Error(message);
      }
      const reply = data.response || "(no response)";
      setMessages((m) => [...m, { role: "assistant", content: reply, ts: Date.now() }]);
      // Refresh the quota badge — only matters for limited tiers but
      // cheap enough to call universally.
      fetchUsage();
    } catch (err) {
      setError(err.message);
      // Drop the user message that didn't get a reply so retry doesn't
      // duplicate it.
      setMessages((m) => m.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendPrompt();
  };

  const handleClear = () => {
    if (!window.confirm("Start a fresh conversation? Your previous messages will be cleared.")) {
      return;
    }
    setMessages([]);
    if (typeof window !== "undefined" && messagesKey) {
      window.localStorage.removeItem(messagesKey);
    }
    if (typeof window !== "undefined" && sessionKey) {
      // New session id so Claude doesn't keep prior context.
      const fresh = `coach_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      window.localStorage.setItem(sessionKey, fresh);
      // Force re-render with new session id (cheap path: reload route)
      window.location.reload();
    }
  };

  // ---------------- Workout Plan Builder handlers ------------------- //
  const openBuilder = (prefill = "") => {
    setBuilderPrompt(prefill);
    setBuilderPlan(null);
    setBuilderError("");
    setBuilderStep("brief");
    setBuilderOpen(true);
  };

  const generatePlan = async () => {
    const body = builderPrompt.trim();
    if (!body || builderGenerating) return;
    setBuilderError("");
    setBuilderGenerating(true);
    try {
      const token = localStorage.getItem("ta_access_token");
      const res = await fetch(`${API_BASE}/api/ai/coach/build-workout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ prompt: body }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const message =
          (data?.detail && typeof data.detail === "object" && data.detail.message) ||
          (typeof data?.detail === "string" && data.detail) ||
          `Request failed (HTTP ${res.status})`;
        throw new Error(message);
      }
      setBuilderPlan(data);
      setBuilderStep("preview");
    } catch (err) {
      setBuilderError(err.message);
    } finally {
      setBuilderGenerating(false);
    }
  };

  const savePlan = async () => {
    if (!builderPlan || builderSaving) return;
    setBuilderSaving(true);
    try {
      const token = localStorage.getItem("ta_access_token");
      const res = await fetch(`${API_BASE}/api/entities/WorkoutPlan`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          ...builderPlan,
          is_active: false,
          visible_on_community: false,
          source: "ai_coach",
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.detail || `Failed to save (HTTP ${res.status})`);
      }
      toast.success("Workout saved to your library", {
        action: {
          label: "View",
          onClick: () => navigate("/workouts"),
        },
      });
      setBuilderOpen(false);
    } catch (err) {
      setBuilderError(err.message);
    } finally {
      setBuilderSaving(false);
    }
  };

  // ------------------------------------------------------------------ //
  // Render: locked state for non-Strength/Elite/trial users
  // ------------------------------------------------------------------ //
  if (!hasAccess) {
    return (
      <div
        className="max-w-2xl mx-auto py-12 text-center"
        data-testid="ai-coach-locked"
      >
        <div className="w-16 h-16 rounded-2xl mx-auto mb-4 bg-amber-500/15 border border-amber-400/30 flex items-center justify-center">
          <Lock className="w-7 h-7 text-amber-300" />
        </div>
        <h1 className="text-2xl font-display font-bold mb-2">AI Coach</h1>
        <p className="text-muted-foreground text-sm leading-relaxed mb-6">
          The AI Coach is included with Strength and Elite Coaching plans.
          Upgrade to ask unlimited questions about training, nutrition,
          recovery, and form.
        </p>
        <Button
          onClick={() =>
            window.open("https://gotafitness.com/pricing", "_blank", "noopener")
          }
          data-testid="ai-coach-upgrade-btn"
          className="bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-black font-semibold"
        >
          See plans
        </Button>
      </div>
    );
  }

  return (
    <div
      className="max-w-2xl mx-auto flex flex-col"
      style={{ height: "calc(100vh - 14rem)" }}
      data-testid="ai-coach-page"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-sky-500 flex items-center justify-center shadow-lg">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-display font-bold leading-tight">AI Coach</h1>
            <p className="text-[11px] text-muted-foreground">
              Claude Sonnet 4.5 · Tuned for fitness
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Quota badge — shown for limited tiers only. Unlimited
              users (Strength / Elite / Trial / Admin) see nothing. */}
          {usage && usage.limit != null ? (
            <div className="flex flex-col items-end gap-0.5">
              <div
                data-testid="ai-coach-quota-badge"
                className={`text-[11px] font-medium px-2 py-1 rounded-md border flex items-center gap-1 ${
                  usage.remaining <= 2
                    ? "bg-rose-500/10 border-rose-400/30 text-rose-300"
                    : "bg-amber-500/10 border-amber-400/30 text-amber-300"
                }`}
              >
                <Zap className="w-3 h-3" />
                {usage.remaining}/{usage.limit} today
              </div>
              <span
                data-testid="ai-coach-reset-label"
                className="text-[9px] uppercase tracking-wider text-muted-foreground/70"
                title={`Daily limit resets at midnight in your local timezone (${userTz})`}
              >
                resets {nextResetLabel}
              </span>
            </div>
          ) : null}
          {messages.length > 0 ? (
            <button
              type="button"
              onClick={handleClear}
              data-testid="ai-coach-clear-btn"
              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 px-2 py-1 rounded-md hover:bg-card transition"
            >
              <RotateCcw className="w-3 h-3" /> Clear
            </button>
          ) : null}
        </div>
      </div>

      <p className="text-xs text-slate-400">AI guidance is unverified; references provide general background, not validation of each response.</p>
      <HealthSourceNote topic="ai" />
      {/* Message thread */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto rounded-2xl border border-border bg-card/40 p-4 space-y-4"
        data-testid="ai-coach-thread"
      >
        {messages.length === 0 ? (
          <EmptyState onPick={(p) => sendPrompt(p)} />
        ) : (
          messages.map((m, i) => <MessageRow key={i} role={m.role} content={m.content} />)
        )}
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground pl-11">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Coach is thinking…
          </div>
        ) : null}
      </div>

      {error ? (
        <div
          className="mt-2 p-2 rounded-lg bg-destructive/10 text-destructive text-xs"
          data-testid="ai-coach-error"
        >
          {error}
        </div>
      ) : null}

      {/* Composer */}
      <form onSubmit={handleSubmit} className="mt-3 flex items-end gap-2">
        <Textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              sendPrompt();
            }
          }}
          placeholder="Ask anything about training, macros, recovery…"
          rows={1}
          className="resize-none min-h-[44px] max-h-32"
          data-testid="ai-coach-input"
        />
        {canBuildPlans && (
          <Button
            type="button"
            variant="outline"
            disabled={loading || builderGenerating}
            onClick={() => openBuilder(prompt)}
            className="h-11 px-3 gap-1.5 shrink-0 border-amber-400/30 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 hover:text-amber-200"
            data-testid="ai-coach-build-plan-btn"
            title="Build a structured workout plan I can save"
          >
            <Dumbbell className="w-4 h-4" />
            <span className="hidden sm:inline text-xs font-medium">Build plan</span>
          </Button>
        )}
        <Button
          type="submit"
          disabled={loading || !prompt.trim()}
          className="h-11 px-4"
          data-testid="ai-coach-send-btn"
        >
          <SendHorizontal className="w-4 h-4" />
        </Button>
      </form>

      {/* Daily-limit upgrade dialog — fires when the backend returns
          429 with reason="daily_limit_reached" (Momentum tier only). */}
      <Dialog open={limitDialogOpen} onOpenChange={setLimitDialogOpen}>
        <DialogContent
          className="max-w-md bg-[#0B1220] border-white/10 text-white"
          data-testid="ai-coach-limit-dialog"
        >
          <DialogHeader>
            <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-[0_0_40px_-8px_rgba(251,191,36,0.6)] mb-2">
              <Crown className="w-7 h-7 text-white" />
            </div>
            <DialogTitle className="font-display text-center text-2xl">
              You&apos;ve hit today&apos;s limit
            </DialogTitle>
            <DialogDescription className="text-white/70 text-center leading-relaxed">
              Momentum includes <span className="font-semibold text-amber-300">
                {usage?.limit ?? 10} AI Coach messages per day
              </span>. Upgrade to <span className="font-semibold text-amber-300">
                Strength
              </span> for unlimited coaching, AI meal scans, and a monthly
              trainer check-in.
              <span
                data-testid="ai-coach-limit-dialog-reset"
                className="block mt-3 text-xs text-white/55"
              >
                Or come back at <span className="text-white/80 font-medium">{nextResetLabel}</span> — your daily messages reset at midnight your time.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button
              type="button"
              onClick={() => {
                window.open(
                  usage?.upgrade_url || "https://gotafitness.com/pricing",
                  "_blank",
                  "noopener"
                );
              }}
              data-testid="ai-coach-limit-upgrade-btn"
              className="w-full bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-black font-semibold"
            >
              Upgrade to Strength
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setLimitDialogOpen(false)}
              data-testid="ai-coach-limit-cancel-btn"
              className="w-full border-white/15 bg-white/[0.04] text-white hover:bg-white/[0.08]"
            >
              Come back tomorrow
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Workout Plan Builder dialog — generates a structured plan via
          /api/ai/coach/build-workout, lets the user review the exercises,
          then POSTs to /api/entities/WorkoutPlan via the save handler. */}
      <Dialog open={builderOpen} onOpenChange={setBuilderOpen}>
        <DialogContent
          className="max-w-lg bg-[#0B1220] border-white/10 text-white max-h-[85vh] overflow-y-auto"
          data-testid="ai-coach-builder-dialog"
        >
          <DialogHeader>
            <div className="mx-auto w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 to-sky-500 flex items-center justify-center shadow-lg mb-1">
              <Wand2 className="w-6 h-6 text-white" />
            </div>
            <DialogTitle className="font-display text-center text-xl">
              {builderStep === "brief" ? "Build me a workout" : builderPlan?.name}
            </DialogTitle>
            <DialogDescription className="text-white/60 text-center text-xs">
              {builderStep === "brief"
                ? "Describe your goal, experience and any equipment. The AI will draft a plan you can save to your library."
                : "Review the plan. You can edit it after saving."}
            </DialogDescription>
          </DialogHeader>

          {builderStep === "brief" ? (
            <div className="space-y-3">
              <Textarea
                rows={4}
                value={builderPrompt}
                onChange={(e) => setBuilderPrompt(e.target.value)}
                placeholder='e.g. "4-day upper/lower split for hypertrophy, 45 min sessions, dumbbells + bench, intermediate"'
                className="bg-white/[0.04] border-white/10 resize-none"
                data-testid="ai-coach-builder-prompt"
              />
              <div className="flex flex-wrap gap-1.5">
                {[
                  "3-day full body for beginners, dumbbells only",
                  "PPL 6-day hypertrophy split, intermediate",
                  "30-min fat loss circuit, no equipment",
                ].map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => setBuilderPrompt(preset)}
                    className="text-[11px] px-2 py-1 rounded-md border border-white/10 bg-white/[0.03] text-white/70 hover:bg-white/[0.08] hover:text-white transition"
                  >
                    {preset}
                  </button>
                ))}
              </div>
              {builderError ? (
                <div
                  className="p-2 rounded-md bg-rose-500/10 text-rose-300 text-xs"
                  data-testid="ai-coach-builder-error"
                >
                  {builderError}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="space-y-3" data-testid="ai-coach-builder-preview">
              <div className="flex flex-wrap gap-1.5">
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border border-amber-400/30 bg-amber-500/10 text-amber-300">
                  {builderPlan?.difficulty}
                </span>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border border-sky-400/30 bg-sky-500/10 text-sky-300">
                  {builderPlan?.goal?.replace(/_/g, " ")}
                </span>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border border-emerald-400/30 bg-emerald-500/10 text-emerald-300">
                  {builderPlan?.body_part_category?.replace(/_/g, " ")}
                </span>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border border-white/15 bg-white/[0.04] text-white/70">
                  {builderPlan?.days_per_week}× / week
                </span>
              </div>
              {builderPlan?.description ? (
                <p className="text-xs text-white/65 leading-relaxed">
                  {builderPlan.description}
                </p>
              ) : null}
              <div className="space-y-2">
                {(builderPlan?.exercises || []).map((ex, i) => (
                  <div
                    key={i}
                    className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2"
                    data-testid={`builder-exercise-${i}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-medium text-sm text-white capitalize">
                        {ex.name}
                      </p>
                      <span className="text-[11px] font-mono shrink-0 text-amber-300">
                        {ex.sets}×{ex.reps}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-[11px] text-white/50 mt-1">
                      <span>rest {ex.rest_seconds}s</span>
                      {ex.notes ? <span className="truncate">· {ex.notes}</span> : null}
                    </div>
                  </div>
                ))}
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-white/50">
                  Plan name (you can rename it)
                </label>
                <Input
                  value={builderPlan?.name || ""}
                  onChange={(e) =>
                    setBuilderPlan((p) => ({ ...(p || {}), name: e.target.value }))
                  }
                  className="mt-1 bg-white/[0.04] border-white/10"
                  data-testid="ai-coach-builder-name-input"
                />
              </div>
              {builderError ? (
                <div className="p-2 rounded-md bg-rose-500/10 text-rose-300 text-xs">
                  {builderError}
                </div>
              ) : null}
            </div>
          )}

          <DialogFooter className="flex-col gap-2 sm:flex-col">
            {builderStep === "brief" ? (
              <Button
                type="button"
                onClick={generatePlan}
                disabled={!builderPrompt.trim() || builderGenerating}
                data-testid="ai-coach-builder-generate-btn"
                className="w-full bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-black font-semibold gap-2"
              >
                {builderGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Designing…
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4" /> Generate workout
                  </>
                )}
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  onClick={savePlan}
                  disabled={builderSaving || !builderPlan?.name?.trim()}
                  data-testid="ai-coach-builder-save-btn"
                  className="w-full bg-gradient-to-r from-emerald-500 to-emerald-400 hover:from-emerald-400 hover:to-emerald-300 text-black font-semibold gap-2"
                >
                  {builderSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Saving…
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" /> Save to my Workouts
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setBuilderStep("brief")}
                  data-testid="ai-coach-builder-retry-btn"
                  className="w-full border-white/15 bg-white/[0.04] text-white hover:bg-white/[0.08]"
                >
                  Try a different brief
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MessageRow({ role, content }) {
  const isUser = role === "user";
  return (
    <div
      className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""}`}
      data-testid={`ai-coach-message-${role}`}
    >
      <div
        className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
          isUser
            ? "bg-sky-500/15 border border-sky-400/30 text-sky-300"
            : "bg-amber-500/15 border border-amber-400/30 text-amber-300"
        }`}
      >
        {isUser ? <UserIcon className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
      </div>
      <div
        className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-line ${
          isUser
            ? "bg-sky-500/15 border border-sky-400/20 text-white"
            : "bg-white/[0.04] border border-white/8 text-foreground"
        }`}
      >
        {content}
        {!isUser && <HealthSourceNote topic="ai" />}
      </div>
    </div>
  );
}

function EmptyState({ onPick }) {
  return (
    <div className="py-6 text-center" data-testid="ai-coach-empty">
      <div className="w-12 h-12 rounded-2xl mx-auto mb-3 bg-amber-500/10 border border-amber-400/20 flex items-center justify-center">
        <Sparkles className="w-5 h-5 text-amber-300" />
      </div>
      <h2 className="text-base font-semibold mb-1">Ask your coach anything</h2>
      <p className="text-xs text-muted-foreground mb-5">
        Tap a prompt below or type your own question.
      </p>
      <div className="grid grid-cols-1 gap-2 max-w-md mx-auto">
        {SUGGESTED_PROMPTS.map((p, i) => (
          <button
            key={p}
            type="button"
            onClick={() => onPick(p)}
            data-testid={suggestionsTestid(i)}
            className="text-left text-sm border border-border bg-card/60 hover:bg-card hover:border-amber-400/30 rounded-xl px-3.5 py-2.5 transition"
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  );
}
